import React, { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { trackLead } from "@/lib/pixel";

interface BookingInquiryFormProps {
  trackingId?: string;
  locationId?: string;
  projectId?: string;
}

const postTrackingEvent = (
  trackingPayload: Record<string, unknown> & {
    formData: Record<string, unknown>;
    formLabels: Record<string, string>;
  },
  options: {
    customFields?: Record<string, { value?: unknown; label: string }>;
  } = {},
) => {
  const { customFields = {} } = options;
  const eventPayload = {
    ...trackingPayload,
    formData: { ...trackingPayload.formData },
    formLabels: { ...trackingPayload.formLabels },
  };
  const body = new FormData();

  for (const [key, field] of Object.entries(customFields)) {
    if (field.value === undefined || field.value === "") continue;
    eventPayload.formData[key] = field.value;
    eventPayload.formLabels[key] = field.label;
  }

  for (const key of Object.keys(eventPayload.formData)) {
    eventPayload.formLabels[key] ||= key;
  }

  body.append("event", JSON.stringify(eventPayload));

  fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
    method: "POST",
    headers: {
      version: "2021-07-28",
    },
    body,
  }).catch(() => {});
};

const EVENT_OPTIONS = [
  "Speaking Engagement",
  "Podcast Guest",
  "Guest Appearance",
  "Event Moderator",
  "Other",
];

const SHOWUP_OPTIONS = ["In Person", "Virtually"];

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  instagram: "",
  facebook: "",
  tiktok: "",
  otherSocial: "",
  website: "",
  eventType: "",
  eventOther: "",
  eventDetails: "",
  showUp: "",
  otherInfo: "",
};

const BookingInquiryForm: React.FC<BookingInquiryFormProps> = ({
  trackingId = "tk_b8bba26f2eb5420481cadf156a166208",
  locationId = "N5B75kjUT85NEZBh2RaZ",
  projectId = "1787853701390843161",
}) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const set = (key: keyof typeof EMPTY_FORM, value: string) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast({
        title: "Missing Information",
        description: "Please fill out your name, email, and phone number.",
        variant: "destructive",
      });
      return;
    }
    if (!formData.eventType || !formData.showUp) {
      toast({
        title: "Missing Information",
        description:
          "Please indicate the event type and how you need Ieshia to show up.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    const resolvedEventType =
      formData.eventType === "Other" && formData.eventOther
        ? `Other: ${formData.eventOther}`
        : formData.eventType;

    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "booking-inquiry-form",
      formData: {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        website: formData.website,
      },
      formLabels: {
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email Address",
        website: "Website",
      },
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: trackingId,
      locationId: locationId,
      projectId: projectId,
      sessionId: crypto.randomUUID(),
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
          ? "mobile"
          : "desktop",
        source: "ai_studio",
        projectId: projectId,
        formName: "Booking Inquiry Form",
      },
    };

    postTrackingEvent(trackingPayload, {
      customFields: {
        Zz1HhaMb4xBwoA5LvLRE: {
          value: formData.instagram,
          label: "Instagram Handle",
        },
        eSxDayPjmIio6t6wPPcC: {
          value: formData.facebook,
          label: "Facebook Handle",
        },
        zatEYP9t9DP8OFNEZwGE: {
          value: formData.tiktok,
          label: "TikTok Handle",
        },
        DZLnVbHger1x4Ipr79PU: {
          value: formData.otherSocial,
          label: "Other Social Handle",
        },
        dqRJHj3Hl57nZ8KWRyuo: {
          value: resolvedEventType,
          label: "Event Requested",
        },
        qeIuf3vV5XXQhUkZYJSl: {
          value: formData.eventDetails,
          label: "Date / Location / Name Of Event",
        },
        "6xzy7HL6vXWBwYEpShG0": {
          value: formData.showUp,
          label: "How Do You Need Ieshia To Show Up",
        },
        eRupt3EGAFfndluJr4Ec: {
          value: formData.otherInfo,
          label: "Any Other Information",
        },
      },
    });

    trackLead({
      content_name: "Booking Inquiry Form",
      content_category: "Speaking Booking",
      status: "submitted",
    });

    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      toast({
        title: "Booking Inquiry Received",
        description:
          "Thank you for inviting Ieshia. We will review and respond shortly.",
      });
    }, 600);
  };

  const inputClass =
    "w-full bg-white/10 border border-white/25 text-white placeholder:text-white/50 rounded-2xl p-3.5 text-sm focus:outline-none focus:border-white/60";
  const labelClass =
    "block text-[11px] font-bold text-white/80 uppercase tracking-wider mb-2";

  return (
    <section id="booking-form" className="bg-[#9E1B50] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5 space-y-4">
          <p className="text-xs font-semibold text-white/70 uppercase tracking-widest">
            Booking inquiry
          </p>
          <h2 className="font-serif text-3xl md:text-5xl font-normal text-white leading-[1.15]">
            Tell us about the room you are creating.
          </h2>
          <p className="text-white/80 text-sm font-light leading-relaxed max-w-sm">
            Share the event vision, audience, date, and desired outcome. We will
            review your request and reach out within 2 business days.
          </p>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div className="bg-white/10 border border-white/20 backdrop-blur-sm text-white rounded-3xl p-8 text-center space-y-4">
              <h3 className="font-serif text-2xl font-normal text-white">
                Inquiry Submitted Successfully!
              </h3>
              <p className="text-white/80 text-sm max-w-md mx-auto font-light">
                Thank you for inviting Ieshia to speak. Our team will review
                your event details and reach out within 2 business days.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData(EMPTY_FORM);
                }}
                className="text-xs font-bold text-white uppercase tracking-wider underline pt-2"
              >
                Submit another inquiry
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white/10 border border-white/20 backdrop-blur-sm rounded-3xl p-6 md:p-8 space-y-5"
            >
              {/* First / Last Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => set("firstName", e.target.value)}
                    placeholder="First name"
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => set("lastName", e.target.value)}
                    placeholder="Last name"
                    required
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className={labelClass}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="you@example.com"
                  required
                  className={inputClass}
                />
              </div>

              {/* Social handles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Instagram</label>
                  <input
                    type="text"
                    value={formData.instagram}
                    onChange={(e) => set("instagram", e.target.value)}
                    placeholder="@yourhandle"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Facebook</label>
                  <input
                    type="text"
                    value={formData.facebook}
                    onChange={(e) => set("facebook", e.target.value)}
                    placeholder="facebook.com/yourhandle"
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>TikTok</label>
                  <input
                    type="text"
                    value={formData.tiktok}
                    onChange={(e) => set("tiktok", e.target.value)}
                    placeholder="@yourhandle"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Other Social</label>
                  <input
                    type="text"
                    value={formData.otherSocial}
                    onChange={(e) => set("otherSocial", e.target.value)}
                    placeholder="Link or handle for any other platform"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Website */}
              <div>
                <label className={labelClass}>Link to Website (Optional)</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => set("website", e.target.value)}
                  placeholder="https://yourwebsite.com"
                  className={inputClass}
                />
              </div>

              {/* Event type */}
              <div>
                <label className={labelClass}>
                  Please indicate event for which you are requesting. *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {EVENT_OPTIONS.map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center gap-3 cursor-pointer rounded-2xl border px-4 py-3 text-sm transition-colors ${
                        formData.eventType === opt
                          ? "border-white/80 bg-white/15 text-white"
                          : "border-white/25 bg-white/5 text-white/80"
                      }`}
                    >
                      <input
                        type="radio"
                        name="eventType"
                        value={opt}
                        checked={formData.eventType === opt}
                        onChange={(e) => set("eventType", e.target.value)}
                        className="accent-white"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
                {formData.eventType === "Other" && (
                  <input
                    type="text"
                    value={formData.eventOther}
                    onChange={(e) => set("eventOther", e.target.value)}
                    placeholder="Please specify"
                    className={`${inputClass} mt-3`}
                  />
                )}
              </div>

              {/* Date / Location / Name of event */}
              <div>
                <label className={labelClass}>
                  Date / Location / Name Of Event *
                </label>
                <input
                  type="text"
                  value={formData.eventDetails}
                  onChange={(e) => set("eventDetails", e.target.value)}
                  placeholder="e.g. October 12, 2026 — Atlanta, GA — Women's Renewal Conference"
                  required
                  className={inputClass}
                />
              </div>

              {/* How do you need Ieshia to show up */}
              <div>
                <label className={labelClass}>
                  How Do You Need Ieshia To Show Up? *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SHOWUP_OPTIONS.map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center gap-3 cursor-pointer rounded-2xl border px-4 py-3 text-sm transition-colors ${
                        formData.showUp === opt
                          ? "border-white/80 bg-white/15 text-white"
                          : "border-white/25 bg-white/5 text-white/80"
                      }`}
                    >
                      <input
                        type="radio"
                        name="showUp"
                        value={opt}
                        checked={formData.showUp === opt}
                        onChange={(e) => set("showUp", e.target.value)}
                        className="accent-white"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>

              {/* Any other information */}
              <div>
                <label className={labelClass}>
                  Any Other Information You Would Like to Share (Optional)
                </label>
                <textarea
                  value={formData.otherInfo}
                  onChange={(e) => set("otherInfo", e.target.value)}
                  placeholder="Tell us about your audience and desired outcome"
                  rows={4}
                  className={`${inputClass} resize-none`}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full md:w-auto bg-white hover:bg-[#FAF6F0] text-[#4A0E26] font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full transition-all"
              >
                {loading ? "Submitting Inquiry..." : "Submit booking inquiry →"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default BookingInquiryForm;
